// launch-outcome.ts — what the wizard's Launch button DOES when the launch call
// does not come back cleanly. Pure (and one thin async race), so it is testable
// at all: jest here is `testEnvironment: 'node'` — no DOM, no React Testing
// Library — so the only way to cover this decision is to keep it out of the
// component. `wizard.tsx` awaits `resolveLaunchOutcome`, hands the result to
// `decideLaunchOutcome`, and applies what comes back without deciding anything
// itself.
//
// THE BUG THIS EXISTS FOR (pre-existing, present in `prod` too — not a
// regression from the #103 branch).
//
//   `handleLaunch` set `submitting` true, awaited the flush PATCH and then
//   `launchDrip`, and reset `submitting` ONLY in `catch`. A request that never
//   settles never reaches `catch`. Observed live during a transient backend
//   slowdown: five stacked `pending` PATCHes to `/drips/<id>`, direct probes at
//   4–6s against a normal 0.3–0.8s. The Launch button was left permanently
//   `disabled` with no error shown, and since `submitting` is component state,
//   stepping between wizard steps does not clear it. Only a full page reload
//   recovered the button.
//
// SO: bound the wait, and make both failure modes recoverable AND legible.
//
// THE HARD PART IS NOT THE TIMEOUT — IT IS THE RETRY.
//
//   A timeout tells you that you stopped waiting. It tells you NOTHING about
//   whether the write landed. That indeterminacy is exactly why no retry path
//   existed before: `launchDrip` mints a fresh step-0 campaign over the whole
//   audience on every call, so a blind retry after a slow-but-successful launch
//   messages every recipient twice.
//
//   It is safe now. The backend takes the launch as an ATOMIC CLAIM guarded on
//   `status ∈ {draft, null}` AND `startedAt` being unset (backend `6de36bf7`) —
//   `startedAt` is stamped BY the claim and is unreachable from the client — so
//   a second launch of an already-launched drip is REFUSED with a Feathers
//   `Conflict` (HTTP 409) and creates nothing.
//
//   That 409 is not a failure to report. It is the ANSWER to the question the
//   timeout left open: the first launch DID land. So `conflict` is a success
//   outcome — navigate to the campaign, show no error.
//
//   The retry re-runs the flush PATCH before asking again, which on a launch
//   that DID land writes `currentStep: 0` onto a now-active drip. That is a
//   no-op there and not a step-pointer regression: the server's own claim
//   stamps `currentStep: 0` in the same breath as `status: 'active'`, so a drip
//   that launched seconds ago is at step 0 anyway. The genuinely dangerous case
//   — relaunching a drip that has since ADVANCED (stale tab, typed URL) — never
//   reaches the patch: `handleLaunch`'s `dripHasLaunched` guard refuses before
//   it, from the drip the wizard re-fetches on mount.

/** How the launch attempt ended, from the caller's point of view. */
export type LaunchOutcome =
  /** Flush + launch both resolved. */
  | 'ok'
  /** We stopped waiting. Says nothing about whether the write landed. */
  | 'timeout'
  /** The server refused a relaunch (409): the drip is ALREADY launched. */
  | 'conflict'
  /** Anything else — a real, definite failure. */
  | 'error';

/**
 * How long the whole launch sequence (flush PATCH + `launchDrip`) may take
 * before the wizard stops waiting.
 *
 * 30s. Sized from measured latency, not from taste:
 *   - healthy: the two calls are ~0.3–0.8s each, so ~1.5s worst case;
 *   - the degraded window that produced this bug measured 4–6s PER call, i.e.
 *     ~12s for the pair — 30s still clears that by 2.5x;
 *   - `launchDrip` is not a trivial write: it resolves the audience across every
 *     board in `boardIDArray` and mints the step-0 campaign, so a large
 *     multi-audience launch is legitimately seconds of server work. The bound
 *     has to sit well above that or a fine launch gets reported as unconfirmed.
 *   - and it is far below "the user gives up": ~30s of a dead button is the
 *     point where a person reloads the tab, which is precisely the unrecoverable
 *     state we are removing.
 *
 * Choosing it too SHORT is now cheap rather than dangerous: an over-eager
 * timeout on a launch that does land is answered by the 409 on the retry, which
 * routes to the campaign as a success. Choosing it too LONG is what hurt — so
 * we err short.
 */
export const LAUNCH_TIMEOUT_MS = 30_000;

/** Shown after a timeout. Deliberately does NOT claim the launch failed — we
 *  do not know that — and it tells the user retrying is safe, because the
 *  server-side claim makes it so. */
export const LAUNCH_TIMEOUT_MESSAGE =
  "We couldn't confirm the launch — the server is taking longer than usual. " +
  'Press Launch again: if it already went through you\'ll be taken straight to the campaign, ' +
  'and nobody is messaged twice.';

/** Shown on a definite failure. The launch claim is rolled back server-side on
 *  a failed launch, so the drip is still a launchable draft. */
export const LAUNCH_FAILED_MESSAGE =
  "The campaign couldn't be launched. Nothing was sent — please try again.";

/**
 * Is this the server refusing a relaunch (409 Conflict), as opposed to any
 * other failure?
 *
 * Matched broadly on purpose. The refusal is thrown as a Feathers `Conflict`
 * from `launchDrip` and reaches the client through `fetchBaseQuery`, so the
 * normal shape is `{status: 409, data: {name: 'Conflict', code: 409}}` — but
 * fetchBaseQuery reports a non-JSON body as
 * `{status: 'PARSING_ERROR', originalStatus: 409}`, and a rejected redux thunk
 * hands back a plain serialized error instead. Reading only `err.status` would
 * let a genuine "already launched" fall through to the error branch and tell the
 * user their live campaign failed to launch.
 */
export function isLaunchConflict(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as Record<string, unknown>;
  const data = (e.data ?? {}) as Record<string, unknown>;
  return (
    e.status === 409 ||
    e.originalStatus === 409 ||
    e.code === 409 ||
    data.code === 409 ||
    e.name === 'Conflict' ||
    data.name === 'Conflict'
  );
}

/**
 * Await the launch sequence, but never longer than `timeoutMs`.
 *
 * The in-flight work is deliberately NOT aborted on timeout: the server is
 * already processing it, so cancelling the client's half would not un-launch
 * anything — it would only throw away the one chance of learning the result. We
 * simply stop waiting. The work's rejection is mapped to a VALUE before the
 * race, so a late failure can never surface as an unhandled promise rejection
 * after the user has moved on.
 */
export async function resolveLaunchOutcome(
  work: Promise<unknown>,
  timeoutMs: number = LAUNCH_TIMEOUT_MS,
): Promise<LaunchOutcome> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  // The timer RESOLVES (never rejects) with the outcome — the work branch can
  // never produce 'timeout', so the race result is unambiguous without a
  // sentinel value.
  const timeout = new Promise<LaunchOutcome>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), timeoutMs);
  });
  const settledWork: Promise<LaunchOutcome> = work.then(
    () => 'ok',
    (err: unknown) => (isLaunchConflict(err) ? 'conflict' : 'error'),
  );

  try {
    const outcome = await Promise.race([settledWork, timeout]);
    if (outcome === 'timeout') {
      // The race is over but `work` is not. `settledWork` already absorbs its
      // rejection (it maps it to a value), so nothing is left unhandled; we
      // simply stop caring about the answer.
      void settledWork;
    }
    return outcome;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** What the wizard should do with an outcome. */
export interface LaunchDecision {
  /** Route to navigate to (always `{replace: true}` at the call site), or
   *  undefined to stay in the wizard. */
  navigateTo?: string;
  /** Inline error to show above the Launch button, or undefined to clear it. */
  errorMessage?: string;
  /** Re-enable the Launch button. False only when we are navigating away. */
  resetSubmitting: boolean;
  /**
   * May the wizard's exit cleanup still reclaim this draft?
   *
   * `launchedRef` exists so the unmount cleanup never deletes a draft that has
   * become a live campaign. On a TIMEOUT we do not know whether it did — and
   * the two mistakes are not symmetric: keeping the flag on a launch that
   * failed leaves a harmless draft in the list, while clearing it on a launch
   * that succeeded points the delete path at a live campaign. So `timeout`
   * keeps it, and only a DEFINITE failure (`error`) releases it — on which the
   * backend has rolled its claim back and the drip really is a draft again.
   */
  keepLaunchedFlag: boolean;
}

/**
 * The whole decision, in one place.
 *
 * `conflict` navigates to the campaign DETAIL (`/campaigns/:id`) rather than the
 * post-launch confirmation screen, matching what the `dripHasLaunched` guard in
 * `handleLaunch` already does for the same situation: this press launched
 * nothing, it discovered that a launch already exists. The detail shows the real
 * state of that campaign, which is the right answer whether the first attempt
 * was this user's timed-out press a moment ago or a launch from a stale tab.
 */
export function decideLaunchOutcome(outcome: LaunchOutcome, dripId: string): LaunchDecision {
  switch (outcome) {
    case 'ok':
      return {
        navigateTo: `/campaigns/${dripId}/launched`,
        resetSubmitting: false,
        keepLaunchedFlag: true,
      };
    case 'conflict':
      return {
        navigateTo: `/campaigns/${dripId}`,
        resetSubmitting: false,
        keepLaunchedFlag: true,
      };
    case 'timeout':
      return {
        errorMessage: LAUNCH_TIMEOUT_MESSAGE,
        resetSubmitting: true,
        keepLaunchedFlag: true,
      };
    case 'error':
    default:
      return {
        errorMessage: LAUNCH_FAILED_MESSAGE,
        resetSubmitting: true,
        keepLaunchedFlag: false,
      };
  }
}
